import { useState } from "react";
import { FileText, Check, CheckCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function resolveAttachmentUrl(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  const base = (typeof window !== "undefined" ? window.location.origin : API_BASE).replace(/\/$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${base}${path}`;
}

function isPdfAttachment(message) {
  return message.attachment_type === "application/pdf"
    || (message.attachment_url && message.attachment_url.toLowerCase().endsWith(".pdf"));
}

function DeliveryStatus({ message, isMine }) {
  if (!isMine) return null;
  const status = message.delivery_status || (message.is_read ? "read" : "sent");

  if (status === "read") {
    return <CheckCheck className="w-3 h-3 text-blue-200 inline-block ml-1" />;
  }
  if (status === "delivered") {
    return <CheckCheck className="w-3 h-3 text-blue-300/80 inline-block ml-1" />;
  }
  if (status === "sending") {
    return <span className="text-[10px] text-blue-200 ml-1">…</span>;
  }
  return <Check className="w-3 h-3 text-blue-200 inline-block ml-1" />;
}

export default function MessageBubble({ message, isMine }) {
  const [imgError, setImgError] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const attachmentSrc = resolveAttachmentUrl(message.attachment_thumb_url || message.attachment_url);
  const fullAttachmentSrc = resolveAttachmentUrl(message.attachment_url);
  const isPdf = isPdfAttachment(message);
  const isPhotoOnly = message.content === "📷 Photo";
  const showText = message.content && (!isPhotoOnly || !attachmentSrc || imgError) && message.content !== "📎 PDF";
  const failed = message.delivery_status === "failed";

  return (
    <div className={`flex w-full min-w-0 max-w-full ${isMine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[min(85%,20rem)] sm:max-w-[min(70%,24rem)] px-4 py-2.5 rounded-2xl text-sm break-words overflow-hidden ${
        failed
          ? "bg-red-100 text-red-800 border border-red-200"
          : isMine
            ? "bg-blue-600 text-white rounded-br-md"
            : "bg-slate-100 text-slate-900 rounded-bl-md"
      }`}>
        {fullAttachmentSrc && isPdf && (
          <a
            href={fullAttachmentSrc}
            target="_blank"
            rel="noreferrer"
            className={`flex items-center gap-2 mb-2 p-2 rounded-lg ${isMine ? "bg-blue-500/50" : "bg-white"}`}
          >
            <FileText className={`w-5 h-5 shrink-0 ${isMine ? "text-blue-100" : "text-red-500"}`} />
            <span className={`text-xs font-medium truncate ${isMine ? "text-white" : "text-slate-700"}`}>
              Document PDF
            </span>
          </a>
        )}
        {attachmentSrc && !isPdf && !imgError && (
          <>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="block mb-2 w-full cursor-zoom-in rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <img
                src={attachmentSrc}
                alt="Pièce jointe"
                loading="lazy"
                className="rounded-lg max-h-48 max-w-full object-cover"
                onError={() => setImgError(true)}
              />
            </button>
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
              <DialogContent className="max-w-[min(96vw,56rem)] w-full p-2 sm:p-3 bg-black/95 border-slate-800 shadow-2xl">
                <DialogTitle className="sr-only">Aperçu de la photo</DialogTitle>
                <img
                  src={fullAttachmentSrc || attachmentSrc}
                  alt="Pièce jointe en grand"
                  className="max-h-[85vh] w-full object-contain rounded-md"
                />
              </DialogContent>
            </Dialog>
          </>
        )}
        {attachmentSrc && !isPdf && imgError && (
          <p className={`text-xs mb-1 ${isMine ? "text-blue-100" : "text-slate-500"}`}>📷 Photo (aperçu indisponible)</p>
        )}
        {showText && <p>{message.content}</p>}
        {!attachmentSrc && (isPhotoOnly || message.content === "📎 PDF") && <p>{message.content}</p>}
        <div className={`flex items-center gap-1 mt-1 ${isMine ? "text-blue-200" : "text-slate-400"}`}>
          <p className="text-[10px]">
            {new Date(message.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
          </p>
          <DeliveryStatus message={message} isMine={isMine} />
        </div>
      </div>
    </div>
  );
}

export function attachmentPreviewLabel(message) {
  if (!message) return "";
  if (message.attachment_type === "application/pdf" || message.content === "📎 PDF") return "📎 PDF";
  if (message.attachment_url || message.content === "📷 Photo") return "📷 Photo";
  return message.content;
}